import { useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { money, type Money } from "../../domain/money/money";
import { Logo } from "../../../theme/Logo";

/**
 * Opening a shift.
 *
 * No sale can start without one (FR-035). This is the first screen of the
 * trading day and it exists to make one number deliberate: the float that every
 * later reconciliation is measured against.
 */

interface ShiftOpenProps {
  currency: CurrencyFormat;
  staffName: string;
  busy?: boolean;
  error?: string | null;
  onOpen(openingFloat: Money): void;
}

const QUICK_FLOATS = [100_000, 200_000, 300_000, 500_000];

export function ShiftOpen({ currency, staffName, busy = false, error = null, onOpen }: ShiftOpenProps) {
  const [entry, setEntry] = useState("");

  const amount = entry === "" ? 0 : Number.parseInt(entry, 10);

  return (
    <main className="shiftopen">
      <Logo variant="lockup" size={36} />

      <div className="shiftopen__panel">
        <p className="shiftopen__who">{staffName}</p>
        <h1 className="shiftopen__title">Open the till</h1>
        <p className="shiftopen__hint">Count the float before you start. Everything today is measured against it.</p>

        <div className="shiftopen__amount money">{formatMoney(amount as Money, currency)}</div>

        <div className="shiftopen__quick">
          {QUICK_FLOATS.map((value) => (
            <button
              key={value}
              type="button"
              className="shiftopen__quick-option"
              onClick={() => setEntry(String(value))}
            >
              {formatMoney(value as Money, currency)}
            </button>
          ))}
        </div>

        <div className="shiftopen__keys">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((key) => (
            <button
              key={key}
              type="button"
              className="shiftopen__key"
              onClick={() => setEntry((c) => (c + key).replace(/^0+(?=\d)/, "").slice(0, 9))}
            >
              {key}
            </button>
          ))}
          <button
            type="button"
            className="shiftopen__key"
            onClick={() => setEntry((c) => (c + "00").replace(/^0+(?=\d)/, "").slice(0, 9))}
          >
            00
          </button>
          <button
            type="button"
            className="shiftopen__key"
            onClick={() => setEntry((c) => (c + "0").replace(/^0+(?=\d)/, "").slice(0, 9))}
          >
            0
          </button>
          <button
            type="button"
            className="shiftopen__key shiftopen__key--muted"
            onClick={() => setEntry((c) => c.slice(0, -1))}
            aria-label="Delete"
          >
            ⌫
          </button>
        </div>

        {error && (
          <p className="shiftopen__error" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="shiftopen__confirm"
          disabled={busy}
          onClick={() => onOpen(money(amount))}
        >
          {busy ? "Opening…" : "Start trading"}
        </button>
      </div>
    </main>
  );
}

export default ShiftOpen;
